import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Building2, MapPin, Users, Brain, Calculator, Landmark, Zap, Shield, Sparkles, Award, Target, Wrench, Flame, Settings, LineChart } from 'lucide-react';
import img1 from "../Images/work.jpg";

const About = () => {
  // Generate structured data for Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gortherm Energy",
    "alternateName": "Gortherm Energy Nepal",
    "url": "https://www.gorthermenergy.com",
    "logo": "https://www.gorthermenergy.com/logo.png",
    "description": "Leading provider of industrial boiler solutions, steam accessories, process engineering, and water treatment services in Nepal since 2015.",
    "foundingDate": "2015",
    "founder": {
      "@type": "Person",
      "name": "Gortherm Energy Founders"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Biratnagar",
      "addressRegion": "Koshi Province",
      "addressCountry": "NP"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "Customer Service",
        "areaServed": "NP",
        "availableLanguage": ["English", "Nepali"]
      }
    ],
    "sameAs": [
      "https://www.facebook.com/gorthermenergy",
      "https://www.linkedin.com/company/gorthermenergy"
    ],
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": 20
    },
    "serviceArea": {
      "@type": "Country",
      "name": "Nepal"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Industrial Energy Solutions",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Industrial Boiler Sales and Service",
            "serviceType": "Boiler Solutions"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Steam Accessories Supply",
            "serviceType": "Steam Systems"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Process Engineering Services",
            "serviceType": "Engineering Solutions"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Water and Waste Treatment",
            "serviceType": "Water Treatment"
          }
        }
      ]
    }
  };

  // Local Business Schema for each branch
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Gortherm Energy - Head Office",
    "image": img1,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Biratnagar",
      "addressRegion": "Koshi Province",
      "addressCountry": "NP"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "26.4525",
      "longitude": "87.2718"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": ["Biratnagar", "Kathmandu", "Birgunj", "Bhairahawa"]
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About Us",
        "item": "https://www.gorthermenergy.com/about"
      }
    ]
  };

  const companyStats = [
    { icon: <Building2 className="h-8 w-8" />, label: "Head Office", value: "Biratnagar" },
    { icon: <MapPin className="h-8 w-8" />, label: "Branches", value: "Kathmandu, Birgunj, Bhairahawa" },
    { icon: <Users className="h-8 w-8" />, label: "Total Employees", value: "20+" },
    { icon: <Brain className="h-8 w-8" />, label: "Skilled Manpower", value: "30" },
    { icon: <Calculator className="h-8 w-8" />, label: "VAT Number", value: "603533367" },
    { icon: <Landmark className="h-8 w-8" />, label: "Bank Partners", value: "Kumari Bank and Laxmi Bank" }
  ];
  
  const services = [
    { 
      icon: <Flame className="h-6 w-6" />, 
      title: "Boiler Sales & Service", 
      description: "Complete industrial boiler solutions with expert installation and maintenance",
      color: "from-sky-400 to-blue-500"
    },
    { 
      icon: <Zap className="h-6 w-6" />, 
      title: "Steam Accessories", 
      description: "Premium quality steam system components for optimal performance",
      color: "from-indigo-400 to-violet-500"
    },
    { 
      icon: <Settings className="h-6 w-6" />, 
      title: "Process Engineering", 
      description: "Advanced process optimization services for maximum efficiency",
      color: "from-blue-400 to-cyan-500"
    },
    { 
      icon: <LineChart className="h-6 w-6" />, 
      title: "Water & Waste Treatment", 
      description: "State-of-the-art water treatment solutions for industrial applications",
      color: "from-violet-400 to-purple-500"
    }
  ];

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>About Gortherm Energy | Industrial Boiler & Energy Solutions Nepal</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Leading industrial energy solutions provider in Nepal since 2015. Expert boiler sales, steam systems, process engineering & water treatment services." />
        
        {/* Enhanced keywords */}
        <meta name="keywords" content="industrial boiler Nepal, steam accessories, process engineering, water treatment, energy solutions, Gortherm Energy, industrial services Nepal, boiler installation, maintenance services" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/about" />
        
        {/* Language attributes */}
        <html lang="en" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="About Gortherm Energy | Nepal's Premier Energy Solutions Provider" />
        <meta property="og:description" content="Established 2015. Leading provider of industrial boiler solutions, steam accessories, and energy services across Nepal with 20+ professionals." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/about" />
        <meta property="og:image" content={img1} />
        <meta property="og:site_name" content="Gortherm Energy" />
        <meta property="og:locale" content="en_NP" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Gortherm Energy | Industrial Energy Solutions Nepal" />
        <meta name="twitter:description" content="Leading industrial energy solutions provider since 2015. Expert services across Nepal." />
        <meta name="twitter:image" content={img1} />
        
        {/* Enhanced robots meta */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Geographic meta tags */}
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Biratnagar, Nepal" />
        
        {/* Business information */}
        <meta name="classification" content="Industrial Energy Services, Boiler Solutions" />
        <meta name="coverage" content="Nepal" />
        
        {/* Structured Data Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Hero Section with SEO-optimized headings */}
        <header className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-blue-900 py-32 overflow-hidden">
          <FloatingElements />
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="flex flex-col items-center space-y-8">
              <div className="flex items-center space-x-4 bg-white/10 px-8 py-4 rounded-full backdrop-blur-sm">
                <Zap className="w-6 h-6 text-blue-400 animate-pulse" />
                <span className="text-white font-semibold tracking-wide">Established 2015</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white text-center tracking-tight">
                About Gortherm Energy
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-blue-100 text-center">
                Nepal's Premier Industrial Energy Solutions Provider
              </h2>
              <p className="text-xl text-blue-100 text-center max-w-3xl leading-relaxed">
                Leading provider of industrial boiler solutions, steam accessories, process engineering, and innovative water treatment services across Nepal since 2015
              </p>
            </div>
          </div>
        </header>

        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-6 lg:px-8 pt-8">
          <ol className="flex items-center space-x-2 text-sm text-gray-600">
            <li><a href="/" className="hover:text-blue-600">Home</a></li>
            <li>/</li>
            <li aria-current="page" className="text-gray-900 font-medium">About Us</li>
          </ol>
        </nav>

        {/* Services Section with proper heading hierarchy */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 -mt-24 relative z-10">
          <section aria-label="Our core services">
            <h2 className="sr-only">Our Core Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <article key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-500 group">
                  <div className={`bg-gradient-to-br ${service.color} p-4 rounded-lg w-fit group-hover:scale-110 transition-all duration-500`}>
                    <div className="text-white">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mt-6 group-hover:text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 mt-3 leading-relaxed">{service.description}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Company Story Section with enhanced content */}
          <section className="mt-32 grid lg:grid-cols-2 gap-16 items-center">
            <article className="space-y-10">
              <div>
                <h2 className="text-blue-600 font-semibold tracking-wide text-sm uppercase">Who We Are</h2>
                <h3 className="text-4xl font-bold mt-3 tracking-tight">Nepal's Premier Energy Solutions Provider</h3>
                <div className="text-gray-700 mt-6 leading-relaxed text-lg space-y-4">
                  <p>
                    Established in 2015, <strong>Gortherm Energy</strong> brings over two decades of expertise to Nepal's industrial energy sector. While our official journey began a decade ago, our team's combined experience spans 20 years of delivering innovative solutions, premium quality products, and unmatched service.
                  </p>
                  <p>
                    As a leading provider of <strong>industrial boiler solutions</strong>, <strong>steam accessories</strong>, <strong>process engineering services</strong>, and <strong>water treatment systems</strong>, we serve businesses across Nepal and internationally. Our headquarters in Biratnagar, along with branches in Kathmandu, Birgunj, and Bhairahawa, ensure comprehensive nationwide coverage.
                  </p>
                  <p>
                    With 20+ dedicated employees and 30 skilled technical professionals, we continue to lead the industry with our commitment to excellence, innovation, and customer satisfaction. Trusted by major industries, we provide complete technical support from installation to maintenance.
                  </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Achievement 
                  icon={<Award className="h-6 w-6" />}
                  title="Industry Leader"
                  description="Forerunner in industrial boiler solutions and energy services since 2015"
                />
                <Achievement 
                  icon={<Target className="h-6 w-6" />}
                  title="Nationwide Service"
                  description="Complete technical support across Nepal and global markets"
                />
              </div>
            </article>
            <div className="relative group">
              <div className="absolute -inset-4">
                <div className="w-full h-full mx-auto opacity-30 blur-lg filter group-hover:opacity-70 transition duration-1000 group-hover:duration-200 animate-pulse bg-gradient-to-r from-blue-600 to-indigo-600"></div>
              </div>
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                <img 
                  src={img1} 
                  alt="Gortherm Energy industrial boiler installation and maintenance services in Nepal"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  width="600"
                  height="600"
                />
              </div>
            </div>
          </section>

          {/* Company Stats Section */}
          <section className="mt-32">
            <h2 className="text-4xl font-bold text-center mb-4">Company Overview</h2>
            <p className="text-center text-gray-600 text-lg mb-16 max-w-3xl mx-auto">
              Gortherm Energy operates across Nepal with multiple branches, skilled workforce, and trusted banking partnerships to deliver exceptional industrial energy solutions.
            </p>
            <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {companyStats.map((stat, index) => (
                <article key={index} className="group bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                  <div className="flex items-center gap-6">
                    <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl text-white group-hover:scale-110 transition-transform duration-500">
                      {stat.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{stat.label}</h3>
                      <p className="text-gray-600 mt-1 text-lg">{stat.value}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <EnhancedTimeline />

          {/* Additional SEO Content Section */}
          <section className="mt-32 bg-white rounded-2xl p-12 shadow-lg">
            <h2 className="text-3xl font-bold text-center mb-8">Why Choose Gortherm Energy?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 text-gray-700">
              <div>
                <h3 className="text-xl font-semibold mb-3">Proven Expertise</h3>
                <p>Over 20 years of combined team experience in industrial energy solutions, boiler systems, and process engineering across Nepal and international markets.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Comprehensive Services</h3>
                <p>Complete industrial solutions including boiler sales, installation, maintenance, steam accessories, process engineering, and water treatment systems.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Nationwide Coverage</h3>
                <p>Strategic locations in Biratnagar (Head Office), Kathmandu, Birgunj, and Bhairahawa ensure prompt service delivery across all regions of Nepal.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Quality Products</h3>
                <p>Premium quality industrial equipment and components from trusted manufacturers, ensuring reliability and long-term performance for your operations.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Expert Team</h3>
                <p>30 skilled technical professionals and 20+ dedicated employees providing specialized expertise in industrial automation and energy systems.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3">Customer Support</h3>
                <p>24/7 technical support, regular maintenance programs, and emergency response services to minimize downtime and maximize productivity.</p>
              </div>
            </div>
          </section>

          {/* Industry Applications */}
          <section className="mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-12">
            <h2 className="text-3xl font-bold text-center mb-4">Industries We Serve</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Providing specialized industrial energy solutions across multiple sectors in Nepal
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-semibold text-lg mb-2">Manufacturing</h3>
                <p className="text-gray-600 text-sm">Industrial boilers and steam systems for production facilities</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-semibold text-lg mb-2">Textile Industry</h3>
                <p className="text-gray-600 text-sm">Steam generation and process optimization solutions</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-semibold text-lg mb-2">Food Processing</h3>
                <p className="text-gray-600 text-sm">Hygiene-focused water treatment and steam systems</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-semibold text-lg mb-2">Pharmaceuticals</h3>
                <p className="text-gray-600 text-sm">High-purity water treatment and process engineering</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-semibold text-lg mb-2">And Many More</h3>
                <p className="text-gray-600 text-sm">With all kind of industrial solutions and products along wiht automation </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

const Achievement = ({ icon, title, description }) => (
  <article className="p-8 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 group hover:-translate-y-1">
    <div className="text-blue-500 mb-4 group-hover:scale-110 transition-transform duration-500">{icon}</div>
    <h4 className="text-xl font-bold mb-3">{title}</h4>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </article>
);

const FloatingElements = () => (
  <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute top-1/2 left-1/4 transform -translate-y-1/2">
      <div className="w-40 h-40 bg-blue-500/10 rounded-full animate-pulse" />
    </div>
    <div className="absolute top-1/3 right-1/4 transform -translate-y-1/2">
      <div className="w-32 h-32 bg-indigo-500/20 rounded-full animate-bounce" />
    </div>
    <div className="absolute bottom-1/4 left-1/3 transform -translate-y-1/2">
      <div className="w-24 h-24 bg-blue-500/15 rounded-full animate-pulse delay-300" />
    </div>
  </div>
);

const EnhancedTimeline = () => (
  <section className="mt-32 pb-32">
    <h2 className="text-4xl font-bold text-center mb-4">Our Journey</h2>
    <p className="text-center text-gray-600 text-lg mb-16 max-w-3xl mx-auto">
      From our establishment in 2015 to becoming Nepal's leading industrial energy solutions provider, discover our milestones and achievements.
    </p>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      {[
        { 
          year: '2015', 
          title: 'Foundation', 
          description: 'Established Gortherm Energy with a vision to revolutionize Nepal\'s industrial energy sector with innovative boiler solutions',
          icon: <Flame className="w-6 h-6" />
        },
        { 
          year: '2017', 
          title: 'Market Leader', 
          description: 'Emerged as the forerunner in industrial boiler sales, installation, and comprehensive maintenance services across Nepal',
          icon: <Award className="w-6 h-6" />
        },
        { 
          year: '2020', 
          title: 'National Expansion', 
          description: 'Extended operations to Kathmandu, Birgunj, and Bhairahawa, establishing comprehensive service network across Nepal',
          icon: <MapPin className="w-6 h-6" />
        },
        { 
          year: '2023', 
          title: 'Innovation & Growth', 
          description: 'Introduced cutting-edge water treatment solutions and advanced process engineering services for industrial clients',
          icon: <Sparkles className="w-6 h-6" />
        }
      ].map((event, index) => (
        <article key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 group hover:-translate-y-1">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-12 h-12 rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-500 mb-6">
            {event.icon}
          </div>
          <time className="text-lg font-semibold text-blue-600 mb-2 block">{event.year}</time>
          <h3 className="text-xl font-bold mb-3">{event.title}</h3>
          <p className="text-gray-600 leading-relaxed">{event.description}</p>
        </article>
      ))}
    </div>
  </section>
);

export default About;